import { type ChangeEvent, type PropsWithChildren, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";

import * as Popover from "@radix-ui/react-popover";
import * as Select from "@radix-ui/react-select";

import * as styles from "./karendaa.module.css";

function pad(n: number) {
    return n < 10 ? `0${n}` : `${n}`;
}
function formatDDMMYYYY(date: Date | null) {
    if (!date) return "";
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}
function parseDDMMYYYY(value: string): Date | null {
    const m = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!m) return null;
    const dd = Number(m[1]);
    const mm = Number(m[2]) - 1;
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, mm, dd);
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm || d.getDate() !== dd) return null;
    return d;
}
const getStartOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const getStartOfDayDate = (year: number, month: number, day: number) => new Date(year, month, day);

// const isSameDay = (a: Date, b: Date) =>
//     a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isSameDayDate = (year: number, month: number, day: number, b: Date) =>
    year === b.getFullYear() && month === b.getMonth() && day === b.getDate();

function buildMonthGrid(year: number, month: number) {
    const firstOfMonth = new Date(year, month, 1);
    const startDow = firstOfMonth.getDay();
    const start = new Date(year, month, 1 - startDow);
    const days: Date[] = [];
    for (let i = 0; i < 42; i++)
        days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    return days;
}

// rehookify

export type KarendaaVariant = "rose" | "blue" | "green" | "magenta";

export interface KarendaaDayPickerProps extends PropsWithChildren {
    minDate?: Date;
    maxDate?: Date;
    inputPlaceHolder?: string;
    defaultValue?: Date | null;
    value?: Date | null;
    onChange?: (date: Date | null) => void;
    variant?: KarendaaVariant;
    className?: string;
    monthNames?: string[];
    weekDays?: string[];
    selectOnlyCurrentMonth?: boolean;
    id?: string;
}

export default function KarendaaDayPicker({
    id,
    minDate: minProp,
    maxDate: maxProp,
    inputPlaceHolder = "DD.MM.YYYY",
    defaultValue = null,
    value: valueProp,
    onChange,
    variant = "magenta",
    className,
    children,
    monthNames,
    weekDays,
    selectOnlyCurrentMonth,
}: KarendaaDayPickerProps) {
    const today = useMemo(() => new Date(), []);
    const minDate = useMemo(() => minProp ?? new Date(1900, 0, 1), [minProp]);
    const maxDate = useMemo(
        () => getStartOfDay(maxProp ?? new Date(today.getFullYear() + 200, 11, 31)),
        [maxProp, today],
    );

    const isControlled = valueProp !== undefined;
    const [internalValue, setInternalValue] = useState<Date | null>(defaultValue);
    const selected: Date | null = isControlled ? (valueProp as Date | null) : internalValue;

    const initialView = useMemo(() => selected ?? today, [selected, today]);
    const [viewDate, setViewDate] = useState<Date>(
        new Date(initialView.getFullYear(), initialView.getMonth(), 1),
    );

    useEffect(() => {
        if (selected) {
            setViewDate(new Date(selected.getFullYear(), selected.getMonth(), 1));
        }
    }, [selected]);

    const clampToBounds = (d: Date) => {
        const minYM = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        const maxYM = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
        if (d < minYM) return minYM;
        if (d > maxYM) return maxYM;
        return d;
    };
    const addMonths = (d: Date, delta: number) =>
        clampToBounds(new Date(d.getFullYear(), d.getMonth() + delta, 1));

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(formatDDMMYYYY(selected));
    useEffect(() => {
        setInputValue(formatDDMMYYYY(selected));
    }, [selected]);

    const displayedYear = viewDate.getFullYear();
    const displayedMonth = viewDate.getMonth();
    const gridDays = useMemo(
        () => buildMonthGrid(displayedYear, displayedMonth),
        [displayedYear, displayedMonth],
    );

    const years = useMemo(() => {
        const out: number[] = [];
        for (let y = maxDate.getFullYear(); y >= minDate.getFullYear(); y--) out.push(y);
        return out;
    }, [minDate, maxDate]);

    const allMonths = useMemo(() => {
        const out: number[] = [];
        if (maxDate.getFullYear() === minDate.getFullYear()) {
            for (let y = maxDate.getMonth(); y >= minDate.getMonth(); y--) out.push(y);
        } else {
            return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
        }

        return out;
    }, [minDate, maxDate]);

    const goToYearMonth = (y: number, m: number) => setViewDate(clampToBounds(new Date(y, m, 1)));
    const handlePrev = () => setViewDate((d) => addMonths(d, -1));
    const handleNext = () => setViewDate((d) => addMonths(d, 1));
    const handleMonthChange = (m: number) => goToYearMonth(displayedYear, m);
    const handleYearChange = (y: number) => goToYearMonth(y, displayedMonth);

    const onPick = (picked: Date) => {
        if (!isControlled) {
            setInternalValue(picked);
        }
        onChange?.(picked);
        setOpen(false);
    };

    const inputId = id || "date";
    const hintId = inputId + "-hint";
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value);
    const handleInputBlur = () => {
        const parsed = parseDDMMYYYY(inputValue);

        if (parsed?.toDateString() == selected?.toDateString()) {
            return;
        }

        if (!parsed) {
            return setInputValue(formatDDMMYYYY(selected));
        }
        const startOfDay = getStartOfDay(parsed);
        if (startOfDay < minDate || startOfDay > maxDate) {
            return setInputValue(formatDDMMYYYY(selected));
        }
        if (!isControlled) {
            setInternalValue(startOfDay);
        }
        onChange?.(startOfDay);
        setViewDate(new Date(startOfDay.getFullYear(), startOfDay.getMonth(), 1));
    };

    return (
        <div className={clsx(styles.container, className)} data-variant={variant}>
            <Popover.Root open={open} onOpenChange={setOpen}>
                <div className={styles.inputWrapper}>
                    {children}

                    <input
                        id={inputId}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onFocus={() => setOpen(true)}
                        placeholder={inputPlaceHolder}
                        inputMode="numeric"
                        aria-describedby={hintId}
                        aria-label="Date"
                        className={styles.input}
                    />

                    <Popover.Anchor className={styles.anchor} />
                </div>

                <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    avoidCollisions={false}
                    className={styles.popoverContent}
                >
                    <div className={styles.popoverInner}>
                        <header className={styles.header}>
                            <button
                                type="button"
                                className={styles.navButton}
                                onClick={handlePrev}
                                aria-label="Previous month"
                            >
                                <ChevronLeft className={styles.navIcon} />
                            </button>

                            <div className={styles.centerControls}>
                                <Select.Root
                                    value={String(displayedMonth)}
                                    onValueChange={(val) => handleMonthChange(Number(val))}
                                >
                                    <Select.Trigger className={styles.selectTriggerMonth} aria-label="Month">
                                        <Select.Value>{monthNames[displayedMonth]}</Select.Value>
                                        <Select.Icon>
                                            <ChevronDown className={styles.selectIcon} />
                                        </Select.Icon>
                                    </Select.Trigger>
                                    <Select.Portal>
                                        <Select.Content
                                            className={styles.selectContentMonth}
                                            position="popper"
                                            sideOffset={6}
                                            data-variant={variant}
                                        >
                                            <Select.ScrollUpButton className={styles.scrollButtonUp}>
                                                <ChevronUp className={styles.selectIcon} />
                                            </Select.ScrollUpButton>
                                            <Select.Viewport className={styles.selectViewport}>
                                                {allMonths.map((m) => (
                                                    <Select.Item
                                                        key={m}
                                                        value={String(m)}
                                                        className={styles.selectItem}
                                                    >
                                                        <div className={styles.selectItemIndicator}>
                                                            <Select.ItemIndicator>
                                                                <Check size={16} />
                                                            </Select.ItemIndicator>
                                                        </div>
                                                        <Select.ItemText>{monthNames[m]}</Select.ItemText>
                                                    </Select.Item>
                                                ))}
                                            </Select.Viewport>
                                            <Select.ScrollDownButton className={styles.scrollButtonDown}>
                                                <ChevronDown className={styles.selectIcon} />
                                            </Select.ScrollDownButton>
                                        </Select.Content>
                                    </Select.Portal>
                                </Select.Root>

                                <Select.Root
                                    value={String(displayedYear)}
                                    onValueChange={(val) => handleYearChange(Number(val))}
                                >
                                    <Select.Trigger className={styles.selectTriggerYear} aria-label="Year">
                                        <Select.Value>{displayedYear}</Select.Value>
                                        <Select.Icon>
                                            <ChevronDown className={styles.selectIcon} />
                                        </Select.Icon>
                                    </Select.Trigger>

                                    <Select.Portal>
                                        <Select.Content
                                            className={styles.selectContentYear}
                                            position="popper"
                                            sideOffset={6}
                                            data-variant={variant}
                                        >
                                            <Select.ScrollUpButton className={styles.scrollButtonUp}>
                                                <ChevronUp className={styles.selectIcon} />
                                            </Select.ScrollUpButton>
                                            <Select.Viewport className={styles.selectViewport}>
                                                {years.map((y) => (
                                                    <Select.Item
                                                        key={y}
                                                        value={String(y)}
                                                        className={styles.selectItem}
                                                    >
                                                        <div className={styles.selectItemIndicator}>
                                                            <Select.ItemIndicator
                                                                className={styles.selectItemIndicator}
                                                            >
                                                                <Check size={16} />
                                                            </Select.ItemIndicator>
                                                        </div>
                                                        <Select.ItemText>{y}</Select.ItemText>
                                                    </Select.Item>
                                                ))}
                                            </Select.Viewport>
                                            <Select.ScrollDownButton className={styles.scrollButtonDown}>
                                                <ChevronDown className={styles.selectIcon} />
                                            </Select.ScrollDownButton>
                                        </Select.Content>
                                    </Select.Portal>
                                </Select.Root>
                            </div>

                            <button
                                type="button"
                                className={styles.navButton}
                                onClick={handleNext}
                                aria-label="Next month"
                            >
                                <ChevronRight className={styles.navIcon} />
                            </button>
                        </header>

                        <div className={styles.weekdaysRow}>
                            {weekDays.map((wd) => (
                                <span key={wd} className={styles.weekdayCell}>
                                    {wd}
                                </span>
                            ))}
                        </div>

                        <div className={styles.daysGrid}>
                            {gridDays.map((day) => {
                                const dateDay = day.getDate();
                                const dateMonth = day.getMonth();
                                const dateYear = day.getFullYear();

                                const daySod = getStartOfDayDate(dateYear, dateMonth, dateDay);
                                const disabled = daySod < minDate || daySod > maxDate;
                                const isOutside = selectOnlyCurrentMonth
                                    ? dateMonth !== displayedMonth
                                    : false;
                                const isToday = isSameDayDate(dateYear, dateMonth, dateDay, today);
                                const isSelected = selected
                                    ? isSameDayDate(dateYear, dateMonth, dateDay, selected)
                                    : false;

                                if (isOutside) {
                                    return (
                                        <span
                                            key={day.toISOString()}
                                            className={styles.dayOutside}
                                            aria-hidden="true"
                                        >
                                            {dateDay}
                                        </span>
                                    );
                                }

                                const className = clsx(
                                    styles.dayButton,
                                    isSelected ? styles.daySelected : null,
                                    isToday && !isSelected ? styles.dayToday : null,
                                    disabled ? styles.dayDisabled : null,
                                );

                                return (
                                    <button
                                        key={day.toISOString()}
                                        className={className}
                                        aria-label={`${dateDay} ${monthNames[dateMonth]} ${dateYear}`}
                                        onClick={() => !disabled && onPick(daySod)}
                                        disabled={disabled}
                                        type="button"
                                    >
                                        {dateDay}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </Popover.Content>
            </Popover.Root>
        </div>
    );
}

KarendaaDayPicker.Trigger = Popover.Trigger;
